import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { proxiedUrlFor, proxiedUrlForArtwork } from "./media-url-utils";
import { getBox, getScaleCoefficient } from "./auto-box-collider";
import { DoubleSide, Mesh, MeshStandardMaterial, PlaneGeometry } from "three";

export const DEFAULT_MEDIUM_URL = "https://nextrealitydigital.blob.core.windows.net/nrd/metaverse/canva-roll-print.glb";
export const DEFAULT_MEDIUM = "Canva Roll Print";

class ArtworkObject extends THREE.Object3D {
  constructor() {
    super();
  }

  async createPlaneArtwork({ imageUrl, scaleXArtwork, scaleYArtwork, artworkInfo, reScale = false }) {
    // Create a plane geometry
    const geometry = new PlaneGeometry(1, 1);

    // Create a basic material that is double-sided
    const material = new MeshStandardMaterial({ side: DoubleSide, transparent: true, toneMapped: false });

    material.transparent = true;
    material.alphaTest = 0;

    // Create a mesh with the geometry and material
    const plane = new Mesh(geometry, material);

    const imageProxiedUrl = imageUrl;

    const textureLoader = new THREE.TextureLoader();
    const texture = await textureLoader.loadAsync(imageProxiedUrl);

    // Apply the texture to the plane's material
    plane.material.map = texture;
    plane.material.needsUpdate = true;
    plane.name = "plane";

    // This makes artwork very bright
    // material.emissive = new THREE.Color(0xffffff); // Bright emissive color
    // material.emissiveMap = texture; // Use the texture itself as the emissive map
    // material.emissiveIntensity = 1; // Adjust the intensity to your preference

    const textureWidth = texture.image.width;
    const textureHeight = texture.image.height;

    const aspectRatio = textureWidth / textureHeight;

    // Set the scaling factor for x and y based on the aspect ratio
    let scaleX, scaleY;
    if (!reScale) {
      if (aspectRatio > 1) {
        scaleX = aspectRatio;
        scaleY = 1;
      } else {
        scaleX = 1;
        scaleY = 1 / aspectRatio;
      }
    }

    // If artworkInfo has measurements then sent the data
    // let realScales = extractDimensionsCm(artworkInfo, scaleX, scaleY);
    if (scaleXArtwork && scaleYArtwork && artworkInfo.dimensionsX && artworkInfo.dimensionsY) {
      scaleX = scaleXArtwork;
      scaleY = scaleYArtwork;
    }

    // Apply the scale to the plane
    plane.scale.set(scaleXArtwork, scaleYArtwork, 1);

    this.add(plane);

    return [scaleX, scaleY];
  }
  async loadModelArtwork({ modelUrl, imageUrl, applyRatio, scaleX = 1, scaleY = 1, reScale }) {
    const loader = new GLTFLoader();

    modelUrl = proxiedUrlFor(modelUrl);
    const gltf = await loader.loadAsync(modelUrl);
    const animations = gltf.animations;

    if (!imageUrl) {
      this.add(gltf.scene);

      return [scaleX, scaleY, animations];
    }

    const textureLoader = new THREE.TextureLoader();
    if (imageUrl.includes("cors-proxy.")) {
      imageUrl = imageUrl;
    } else {
      imageUrl = proxiedUrlForArtwork(imageUrl);
    }

    const texture = await textureLoader.loadAsync(imageUrl);

    const textureWidth = texture.image.width;
    const textureHeight = texture.image.height;

    const aspectRatio = textureWidth / textureHeight;

    // // Set the scaling factor for x and y based on the aspect ratio
    if (!reScale) {
      if (aspectRatio > 1) {
        scaleX = aspectRatio;
        scaleY = 1;
      } else {
        scaleX = 1;
        scaleY = 1 / aspectRatio;
      }
    }

    gltf.scene.traverse(node => {
      if (node.isMesh && node.material && node.material.name === "front") {
        node.material.map = texture;
        // Material SRGBColorSpace  to show more realistic colors
        node.material.map.colorSpace = THREE.SRGBColorSpace;

        // Prevent flipped image
        node.material.map.flipY = false;

        node.material.needsUpdate = true;
      }
    });

    if (applyRatio) {
      gltf.scene.scale.set(scaleX, scaleY, 1);
    }

    this.add(gltf.scene);

    return [scaleX, scaleY];
  }

  async setFrame(medium, frameUrl) {
    const loader = new GLTFLoader();

    frameUrl = proxiedUrlFor(frameUrl);
    const gltf = await loader.loadAsync(frameUrl);

    const model = gltf.scene;
    model.name = "frame";
    const parentModel = medium.children[0];

    const width = parentModel.scale.x;
    const height = parentModel.scale.y;
    const offsetX = width * 0.5;
    const offsetY = height * 0.5;
    const cornerSize = 2.935;

    const target = new AFRAME.THREE.Vector3();

    model.traverse(node => {
      if (!node.isMesh) {
        return;
      }

      const boundingBox = node.geometry.boundingBox.getSize(target);

      switch (node.name) {
        // Top left corner
        case "corner_top_left":
          node.position.set(-offsetX, offsetY, 0);
          node.visible = true;
          break;
        // Top right corner
        case "corner_top_right":
          node.position.set(offsetX, offsetY, 0);
          node.visible = true;
          break;
        // Bottom left corner
        case "corner_bottom_left":
          node.position.set(-offsetX, -offsetY, 0);
          node.visible = true;
          break;
        // Bottom right corner
        case "corner_bottom_right":
          node.position.set(offsetX, -offsetY, 0);
          node.visible = true;
          break;
        // Top
        case "center_top":
          node.position.set(0, offsetY, 0);
          node.scale.x = width / boundingBox.x - cornerSize;
          node.visible = true;
          break;
        // Left
        case "center_left":
          node.position.set(-offsetX, 0, 0);
          node.scale.y = height / boundingBox.y - cornerSize;
          node.visible = true;
          break;
        // Bottom
        case "center_bottom":
          node.position.set(0, -offsetY, 0);
          node.scale.x = width / boundingBox.x - cornerSize;
          node.visible = true;
          break;
        // Right
        case "center_right":
          node.position.set(offsetX, 0, 0);
          node.scale.y = height / boundingBox.y - cornerSize;
          node.visible = true;
          break;
      }

      // Duplicate the material because it's shared between the frame
      // pieces
      // Duplication of texture has been removed because it cause issues in
      // Metaverse GLTFLoader
      const material = node.material.clone();
      const texture = material.map;
      texture.anisotropy = 8;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(node.scale.x, node.scale.y);

      // Flip texture repeat values for vertical pieces
      if (["center_left", "center_right"].includes(node.name)) {
        texture.repeat.set(node.scale.y, node.scale.x);
      }

      texture.needsUpdate = true;
      material.map = texture;
      material.toneMapped = false;
      node.material = material;
    });

    model.position.set(0, 0, 0.008);

    this.add(model);
  }
}

export class ArtworkInfo {
  constructor({
    title,
    author,
    authorBirthDeath,
    category,
    artworkDate,
    medium,
    dimensions,
    description,
    dimensionsX,
    dimensionsY,
    dimensionsZ,
    artistStatement,
    metadata
  }) {
    this.title = title;
    this.author = author;
    this.authorBirthDeath = authorBirthDeath;
    this.category = category;
    this.artworkDate = artworkDate ?? "";
    this.medium = medium;
    this.dimensions = dimensions;
    this.description = description;
    this.dimensionsX = dimensionsX;
    this.dimensionsY = dimensionsY;
    this.dimensionsZ = dimensionsZ;
    this.artistStatement = artistStatement;
    this.metadata = metadata;
  }

  toObject() {
    return {
      title: this.title,
      author: this.author,
      authorBirthDeath: this.authorBirthDeath,
      category: this.category,
      artworkDate: this.artworkDate,
      medium: this.medium,
      dimensions: this.dimensions,
      dimensionsX: this.dimensionsX,
      dimensionsY: this.dimensionsY,
      dimensionsZ: this.dimensionsZ,
      description: this.description,
      artistStatement: this.artistStatement,
      metadata: this.metadata
    };
  }
}

export function createBoundingBoxFromObject(obj) {
  return new AFRAME.THREE.Box3().setFromObject(obj);
}

export async function getGlbFromScene(scene, animations) {
  const exporter = new GLTFExporter();
  const options = { binary: true };
  if (animations && animations.length > 0) {
    options.animations = animations;
  }

  const glb = await new Promise((resolve, reject) => {
    exporter.parse(
      scene,
      gltf => resolve(gltf),
      error => {
        console.error("Exporter error:", error);
        reject(error);
      },
      options
    );
  });

  // Create a Uint8Array view from the ArrayBuffer
  const uint8Array = new Uint8Array(glb);

  // Create a Blob from the Uint8Array
  const blob = new Blob([uint8Array]);

  return blob;
}

export async function getModelOnly({
  imageUrl,
  frameUrl,
  artworkInfo,
  mediumName,
  glbModel,
  qrCodeId,
  scaleXArtwork = null,
  scaleYArtwork = null,
  isImage = false,
  resizeOnInit = false,
  scaleZ,
  initialScaleX,
  initialScaleY,
  initialScaleZ,
  scaleFactorX,
  scaleFactorY,
  scaleFactorZ,
  myMediaId,
  isDigitalFile,
  reScale = false,
  isArtwork = true
}) {
  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  const scene = new THREE.Scene();
  let animations;

  const model = new ArtworkObject();
  scene.add(model);
  let scaleX, scaleY;
  if (isDigitalFile) {
    let [scaleXPlane, scaleYPlane] = await model.createPlaneArtwork({
      imageUrl,
      artworkInfo,
      scaleXArtwork,
      scaleYArtwork,
      reScale
    });

    scaleX = scaleXPlane;
    scaleY = scaleYPlane;
  } else {
    let [scaleXModel, scaleYModel, animationsModel] = await model.loadModelArtwork({
      modelUrl: glbModel,
      imageUrl,
      applyRatio: true,
      reScale,
      scaleX: scaleXArtwork,
      scaleY: scaleYArtwork
    });

    scaleX = scaleXModel;
    scaleY = scaleYModel;

    if (animationsModel) {
      scene.animationsModel = animationsModel;
    }

    animations = animationsModel;
  }

  if (scaleXArtwork && scaleYArtwork) {
    scaleX = scaleXArtwork;
    scaleY = scaleYArtwork;
  }
  if (frameUrl) {
    const frameWithMedium = new ArtworkObject();
    await frameWithMedium.setFrame(model, frameUrl);
    scene.add(frameWithMedium);
  }

  scene.userData.metadata = {
    isArtwork,
    imageTexture: imageUrl,
    artworkInfo,
    glbModel,
    mediumName,
    scaleX,
    scaleY,
    completed: true,
    frame: frameUrl,
    qrCodeId,
    resizeOnInit,
    scaleZ,
    initialScaleX,
    initialScaleY,
    initialScaleZ,
    scaleFactorX,
    scaleFactorY,
    scaleFactorZ,
    myMediaId,
    isDigitalFile: isDigitalFile && !glbModel
  };

  const renderer = new THREE.WebGLRenderer();
  renderer.setSize(800, 600);
  renderer.render(scene, camera);

  const glbBlob = await getGlbFromScene(scene, animations);

  return glbBlob;
}

export async function getModel(entry) {
  const author = entry.artistDisplayName || entry.repository || "-";
  const authorBirthDeath = entry?.author?.death || entry.artistDisplayBio;
  const artworkDate = entry.artworkDate || entry.date || "";
  const title = entry.title;
  const category = entry?.category?.name || entry.department;
  const medium = entry.medium;

  const imageUrl = proxiedUrlForArtwork(entry.primaryImageSmall);

  const artworkInfo = new ArtworkInfo({
    author,
    authorBirthDeath,
    title,
    artworkDate,
    category,
    medium
  }).toObject();

  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  const scene = new THREE.Scene();

  const textureModel = new ArtworkObject();
  const [scaleX, scaleY] = await textureModel.loadModelArtwork({
    modelUrl: DEFAULT_MEDIUM_URL,
    imageUrl
  });
  scene.add(textureModel);

  const renderer = new THREE.WebGLRenderer();
  renderer.setSize(entry.width, entry.height);
  renderer.render(scene, camera);

  scene.userData.metadata = {
    isArtwork: true,
    imageTexture: imageUrl,
    artworkInfo,
    glbModel: DEFAULT_MEDIUM_URL,
    mediumName: DEFAULT_MEDIUM,
    scaleX,
    scaleY
  };

  return await getGlbFromScene(scene);
}

export function getArtworkInfo({
  title,
  author,
  authorBirthDeath,
  category,
  artworkDate,
  medium,
  dimensions,
  assetId,
  description,
  dimensionsX,
  dimensionsY,
  dimensionsZ,
  artistStatement,
  metadata
}) {
  const artworkInfo = new ArtworkInfo({
    title,
    author,
    authorBirthDeath,
    category,
    artworkDate,
    medium,
    dimensions,
    assetId,
    description,
    dimensionsX,
    dimensionsY,
    dimensionsZ,
    artistStatement,
    metadata
  });

  return artworkInfo.toObject();
}

export function extractDimensionsCm(artworkInfo, scaleX, scaleY) {
  if (artworkInfo.measurements) {
    const heightCm = artworkInfo.measurements.Height;
    let widthCm = artworkInfo.measurements.Width;

    if (!widthCm) {
      //calculate with third rule form
      widthCm = (heightCm * scaleX) / scaleY;
    }

    return { heightCm, widthCm };
  }

  if (artworkInfo.dimensions) {
    const str = artworkInfo.dimensions;

    // Pattern for 'sheet' dimensions
    const sheetPattern = /sheet:\s*([\d.]+)\s*×\s*([\d.]+)\s*cm/;
    // Direct centimeter measurements pattern
    const directCmPattern = /([\d.]+)\s*x\s*([\d.]+)\s*cm/;
    // Pattern to extract centimeter measurements from a mixed string
    const mixedCmPattern = /\(([\d.]+)\s*x\s*([\d.]+)(?:\s*x\s*([\d.]+))?\s*cm\)/;
    let match = str.match(mixedCmPattern);
    if (!match) {
      match = str.match(sheetPattern);
    }
    if (!match) {
      match = str.match(directCmPattern);
    }

    if (match) {
      // Interpreting the first extracted value as height and the second as width
      const heightCm = parseFloat(match[1]);
      const widthCm = parseFloat(match[2]);
      const depthCm = match[3] ? parseFloat(match[3]) : null;
      return { heightCm, widthCm, depthCm };
    }
  }
  return null;
}

export function convertDimensionsInchesToMeters(x, y, z) {
  const inchesToMeters = 0.0254; // Conversion factor from inches to meters

  const xM = x * inchesToMeters;
  const yM = y * inchesToMeters;
  const zM = z ? z * inchesToMeters : null;

  return { xM, yM, zM };
}

export function convertDimensionsMetersToInches(x, y, z) {
  const metersToInches = 1 / 0.0254; // Conversion factor from meters to inches

  const xI = +(x * metersToInches).toFixed(2);
  const yI = +(y * metersToInches).toFixed(2);
  const zI = z ? +(z * metersToInches).toFixed(2) : null;

  return { xI, yI, zI };
}
export const updateScale = (function () {
  const center = new THREE.Vector3();
  const originalMeshMatrix = new THREE.Matrix4();
  const desiredObjectMatrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const box = new THREE.Box3();
  return function (
    el,
    scaleX,
    scaleY,
    scaleZ,
    initialScaleX,
    initialScaleY,
    initialScaleZ,
    scaleCoefficientX,
    scaleCoefficientY,
    scaleCoefficientZ
  ) {
    el?.object3D.updateMatrices();
    const mesh = el.object3DMap.mesh;

    mesh.updateMatrices();
    mesh.scale.set(initialScaleX, initialScaleY, initialScaleZ);
    // else {
    // Move the mesh such that the center of its bounding box is in the same position as the parent matrix position
    const box = getBox(el, mesh);
    // if(!scaleCoefficientX)
    scaleCoefficientX = getScaleCoefficient(scaleX, box);
    // if(!scaleCoefficientY)
    scaleCoefficientY = getScaleCoefficient(scaleY, box);
    // if(!scaleCoefficientZ)
    scaleCoefficientZ = getScaleCoefficient(scaleZ, box);

    const { min, max } = box;
    // mesh.scale.multiply(new THREE.Vector3( scaleCoefficientX,  scaleCoefficientY,  scaleCoefficientZ));
    mesh.scale.set(
      initialScaleX * scaleCoefficientX,
      initialScaleY * scaleCoefficientY,
      initialScaleZ * scaleCoefficientZ
    );
    center.addVectors(min, max).multiplyScalar(0.5);

    mesh.position.sub(center);
    mesh.matrixNeedsUpdate = true;

    return { scaleCoefficientX, scaleCoefficientY, scaleCoefficientZ };
  };
})();

export const updateScale2 = (function () {
  const center = new THREE.Vector3();
  const originalMeshMatrix = new THREE.Matrix4();
  const desiredObjectMatrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const box = new THREE.Box3();
  return function (
    el,
    scaleX,
    scaleY,
    scaleZ,
    initialScaleX,
    initialScaleY,
    initialScaleZ,
    scaleCoefficientX,
    scaleCoefficientY,
    scaleCoefficientZ
  ) {
    el?.object3D.updateMatrices();
    const mesh = el.object3DMap.mesh;
    mesh.updateMatrices();
    // else {
    // Move the mesh such that the center of its bounding box is in the same position as the parent matrix position
    const box = getBox(el, mesh);
    if (!scaleCoefficientX) scaleCoefficientX = getScaleCoefficient(scaleX, box);
    if (!scaleCoefficientY) scaleCoefficientY = getScaleCoefficient(scaleY, box);
    if (!scaleCoefficientZ) scaleCoefficientZ = getScaleCoefficient(scaleZ, box);

    return { scaleCoefficientX, scaleCoefficientY, scaleCoefficientZ };
  };
})();
